import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { InitialsAvatar } from "@/components/InitialsAvatar";
import { TypeBadge, TemperatureBadge } from "@/components/Badges";
import { EmptyState } from "@/components/EmptyState";
import { StatSkeleton, ListSkeleton } from "@/components/Skeletons";
import { Button } from "@/components/ui/button";
import { relativeDate } from "@/lib/format";
import { ArrowRight, Compass, Flame, Trophy } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/i18n";

function Stat({ label, value, testId }) {
    return (
        <div className="rounded-2xl border border-nm-line bg-nm-surface p-5" data-testid={testId}>
            <p className="text-xs font-medium uppercase tracking-wider text-nm-muted">{label}</p>
            <p className="mt-2 text-3xl font-bold tabular-nums text-nm-ink">{value}</p>
        </div>
    );
}

export default function Dashboard() {
    const { user } = useAuth();
    const { t } = useT();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/dashboard").then(({ data }) => setData(data)).catch((e) => {
            setError(errMsg(e));
            toast.error(errMsg(e));
        });
    }, []);

    if (error) {
        return <EmptyState title="No hemos podido cargar tu panel" description={error}
            action={<Button onClick={() => window.location.reload()}>Reintentar</Button>} />;
    }

    if (!data) {
        return (
            <div className="space-y-8">
                <div className="h-9 w-64 skeleton rounded-lg" />
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                    {Array.from({ length: 4 }).map((_, i) => <StatSkeleton key={i} />)}
                </div>
                <ListSkeleton rows={3} />
            </div>
        );
    }

    const { greeting, stats, acciones_hoy, oportunidades, actividad_reciente, ruta, gamificacion } = data;

    return (
        <div className="space-y-10" data-testid="dashboard">
            <div>
                <p className="text-sm text-nm-muted">{greeting}</p>
                <h1 className="mt-1 text-3xl font-bold tracking-tight text-nm-ink sm:text-4xl">
                    {user?.name?.split(" ")[0]}, esto es lo que toca hoy
                </h1>
            </div>

            {/* Métricas */}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <Stat label="Leads activos" value={stats.leads_activos} testId="stat-leads" />
                <Stat label="Clientes" value={stats.clientes} testId="stat-clientes" />
                <Stat label="Equipo" value={stats.equipo} testId="stat-equipo" />
                <Stat label="Seguimientos" value={stats.seguimientos_pendientes} testId="stat-seguimientos" />
            </div>

            {/* Gamificación: racha + nivel */}
            {gamificacion && (
                <div className="grid gap-4 sm:grid-cols-2" data-testid="gamification-strip">
                    <div className="flex items-center gap-4 rounded-2xl border border-nm-line bg-nm-surface p-5">
                        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-nm-pink/15 text-nm-pink">
                            <Flame className="h-6 w-6" />
                        </div>
                        <div>
                            <p className="text-xs font-medium uppercase tracking-wider text-nm-muted">{t("gam.streak")}</p>
                            <p className="text-2xl font-bold tabular-nums text-nm-ink" data-testid="streak-value">
                                {gamificacion.streak} <span className="text-base font-medium text-nm-muted">{t("gam.days")}</span>
                            </p>
                        </div>
                        <div className="ml-auto text-right">
                            <p className="text-xs text-nm-muted">{t("gam.best")}</p>
                            <p className="font-semibold tabular-nums text-nm-ink">{gamificacion.best_streak}</p>
                        </div>
                    </div>
                    <div className="rounded-2xl border border-nm-line bg-nm-surface p-5" data-testid="level-card">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Trophy className="h-4 w-4 text-nm-accent-txt" />
                                <p className="text-sm font-semibold text-nm-ink">{t("gam.level")} {gamificacion.level}</p>
                            </div>
                            <p className="text-xs text-nm-muted tabular-nums">{gamificacion.xp} XP</p>
                        </div>
                        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-nm-surface-3">
                            <div className="h-full rounded-full bg-nm-accent transition-all duration-500" style={{ width: `${gamificacion.level_progress}%` }} />
                        </div>
                        <p className="mt-2 text-xs text-nm-muted tabular-nums">{gamificacion.next_level_xp - gamificacion.xp} {t("gam.xp_to_next")}</p>
                    </div>
                </div>
            )}

            <div className="grid gap-8 lg:grid-cols-3">
                {/* Acciones de hoy + oportunidades */}
                <div className="space-y-8 lg:col-span-2">
                    <section>
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-nm-ink">Tus acciones de hoy</h2>
                            <Link to="/app/contactos" className="text-sm font-medium text-nm-accent">Ver todos</Link>
                        </div>
                        {acciones_hoy.length === 0 ? (
                            <EmptyState testId="acciones-empty" title="Todo al día"
                                description="No tienes acciones urgentes. Buen momento para sumar contactos nuevos."
                                action={<Link to="/app/contactos"><Button>Añadir contactos</Button></Link>} />
                        ) : (
                            <div className="space-y-3" data-testid="daily-actions-list">
                                {acciones_hoy.map((a) => (
                                    <Link key={a.contact_id} to={`/app/contactos/${a.contact_id}`}
                                        data-testid={`action-item-${a.contact_id}`}
                                        className="flex items-center gap-4 rounded-2xl border border-nm-line bg-nm-surface p-4 transition-colors duration-150 hover:border-nm-accent/40">
                                        <InitialsAvatar name={a.name} />
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate font-semibold text-nm-ink">{a.name}</p>
                                            <p className="truncate text-sm text-nm-muted">{a.action}</p>
                                        </div>
                                        <TypeBadge type={a.type} className="hidden sm:inline-flex" />
                                        <ArrowRight className="h-4 w-4 shrink-0 text-nm-muted" />
                                    </Link>
                                ))}
                            </div>
                        )}
                    </section>

                    <section>
                        <h2 className="mb-4 text-lg font-semibold text-nm-ink">Oportunidades que necesitan seguimiento</h2>
                        {oportunidades.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-nm-line bg-nm-surface p-8 text-center text-sm text-nm-muted">
                                Ninguna oportunidad pendiente ahora mismo.
                            </div>
                        ) : (
                            <div className="divide-y divide-nm-line rounded-2xl border border-nm-line bg-nm-surface">
                                {oportunidades.map((c) => (
                                    <Link key={c.id} to={`/app/contactos/${c.id}`}
                                        className="flex items-center gap-4 p-4 transition-colors duration-150 hover:bg-nm-accent-soft/40">
                                        <InitialsAvatar name={c.name} size="sm" />
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-sm font-semibold text-nm-ink">{c.name}</p>
                                            <p className="truncate text-xs text-nm-muted">{c.next_action}</p>
                                        </div>
                                        <TemperatureBadge temperature={c.temperature} className="hidden sm:inline-flex" />
                                        <span className="shrink-0 text-xs text-nm-muted">
                                            {c.last_contacted_at ? relativeDate(c.last_contacted_at) : "Sin contactar"}
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </section>
                </div>

                {/* Columna derecha: ruta + actividad */}
                <div className="space-y-8">
                    <section className="rounded-2xl border border-nm-line bg-nm-panel p-6 text-white">
                        <div className="flex items-center gap-2 text-white/70">
                            <Compass className="h-4 w-4" />
                            <span className="text-xs font-medium uppercase tracking-wider">Tu ruta</span>
                        </div>
                        <p className="mt-4 text-4xl font-bold tabular-nums">{ruta.progreso}%</p>
                        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-nm-surface/15">
                            <div className="h-full rounded-full bg-nm-surface transition-all duration-500" style={{ width: `${ruta.progreso}%` }} />
                        </div>
                        {ruta.siguiente_paso ? (
                            <div className="mt-5">
                                <p className="text-xs text-white/60">Siguiente paso · Nivel {ruta.siguiente_paso.level}</p>
                                <p className="mt-1 font-semibold">{ruta.siguiente_paso.title}</p>
                            </div>
                        ) : (
                            <p className="mt-5 text-sm text-white/70">¡Has completado toda tu ruta! Sigue duplicando.</p>
                        )}
                        <Link to="/app/ruta" className="mt-5 inline-block">
                            <Button className="btn-grad text-white hover:opacity-90" data-testid="dashboard-route-button">
                                Ir a mi ruta <ArrowRight className="ml-1 h-4 w-4" />
                            </Button>
                        </Link>
                    </section>

                    <section>
                        <h2 className="mb-4 text-lg font-semibold text-nm-ink">Actividad reciente</h2>
                        {actividad_reciente.length === 0 ? (
                            <p className="rounded-2xl border border-dashed border-nm-line bg-nm-surface p-6 text-center text-sm text-nm-muted">
                                Aún no hay actividad. Registra tu primera conversación.
                            </p>
                        ) : (
                            <div className="space-y-4">
                                {actividad_reciente.map((a, i) => (
                                    <div key={i} className="flex gap-3">
                                        <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-nm-accent" />
                                        <div className="min-w-0">
                                            <p className="text-sm text-nm-ink">{a.note}</p>
                                            <p className="mt-0.5 text-xs text-nm-muted">{relativeDate(a.created_at)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
}
